import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Zap, Flame, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StreakMilestonePopup, getReachedMilestone } from '@/components/StreakMilestones';

import { useProgress } from '@/hooks/useProgress';
import { useAuth } from '@/hooks/useAuth';
import { lastAttemptedLessonKey } from '@/lib/deviceProgressState';
import { learningPaths } from '@/data/lessons';
import { soccerLearningPaths } from '@/data/soccerLessons';
import { basketballLearningPaths } from '@/data/basketballLessons';
import { baseballLearningPaths } from '@/data/baseballLessons';
import { useAchievements } from '@/hooks/useAchievements';
import { useSportSkillLevel } from '@/hooks/useSportSkillLevel';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import type { TabId } from '@/components/BottomNav';
import { CoachBanner } from '@/components/CoachBanner';
import { HeartsDisplay } from '@/components/HeartsDisplay';
import { WeeklyRecapCard } from '@/components/WeeklyRecapCard';



interface HomeTabProps {
  onSelectLesson: (id: string) => void;
  onNavigate: (tab: TabId) => void;
  preferredSport?: string;
}

const sports = [
  { id: 'football', emoji: '🏈', label: 'Football', available: true },
  { id: 'soccer', emoji: '⚽', label: 'Soccer', available: true },
  { id: 'basketball', emoji: '🏀', label: 'Basketball', available: true },
  { id: 'baseball', emoji: '⚾', label: 'Baseball', available: true },
] as const;

const sportThemes: Record<string, { gradient: string; btnGradient: string; ring: string; pillBg: string; pillActive: string; pillText: string }> = {
  football: {
    gradient: 'from-primary/20 via-primary/8 to-amber-500/10',
    btnGradient: 'bg-primary hover:bg-primary/90',
    ring: 'ring-primary/30',
    pillBg: 'bg-amber-500/10 hover:bg-amber-500/15 text-amber-700 dark:text-amber-300',
    pillActive: 'bg-gradient-to-r from-primary to-amber-500 text-primary-foreground shadow-md shadow-primary/30 ring-2 ring-primary/40',
    pillText: 'text-amber-600 dark:text-amber-300',
  },
  soccer: {
    gradient: 'from-emerald-500/20 via-emerald-500/8 to-emerald-600/4',
    btnGradient: 'bg-emerald-600 hover:bg-emerald-700',
    ring: 'ring-emerald-400/30',
    pillBg: 'bg-emerald-500/10 hover:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    pillActive: 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30 ring-2 ring-emerald-400/40',
    pillText: 'text-emerald-600 dark:text-emerald-300',
  },
  basketball: {
    gradient: 'from-orange-500/20 via-orange-500/8 to-orange-600/4',
    btnGradient: 'bg-orange-600 hover:bg-orange-700',
    ring: 'ring-orange-400/30',
    pillBg: 'bg-orange-500/10 hover:bg-orange-500/15 text-orange-700 dark:text-orange-300',
    pillActive: 'bg-orange-600 text-white shadow-md shadow-orange-600/30 ring-2 ring-orange-400/40',
    pillText: 'text-orange-600 dark:text-orange-300',
  },
  baseball: {
    gradient: 'from-[#1B2A4E]/25 via-[#C8443A]/10 to-[#FAF6EE]/5',
    btnGradient: 'bg-[#C8443A] hover:bg-[#B23B33]',
    ring: 'ring-[#C8443A]/30',
    pillBg: 'bg-[#C8443A]/10 hover:bg-[#C8443A]/15 text-[#C8443A] dark:text-[#E68A82]',
    pillActive: 'bg-gradient-to-r from-[#1B2A4E] to-[#C8443A] text-white shadow-md shadow-[#C8443A]/30 ring-2 ring-[#C8443A]/40',
    pillText: 'text-[#C8443A] dark:text-[#E68A82]',
  },
};

function getPathsForSport(sport: string) {
  if (sport === 'soccer') return soccerLearningPaths;
  if (sport === 'basketball') return basketballLearningPaths;
  if (sport === 'baseball') return baseballLearningPaths;
  return learningPaths;
}

type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | null;

interface NextLessonContext {
  skillLevel: SkillLevel;
  streak: number;
  hasDoneLessonToday: boolean;
}

/**
 * Choose the lesson the Hero CTA should open.
 *
 * Priority:
 *   1. Resume the last-attempted lesson if it's still incomplete and unlocked.
 *   2. Continue inside the user's CURRENT PATH (the path of the last-attempted
 *      lesson) before jumping to a different path.
 *   3. Skill-aware default starting path:
 *        - beginner       → first path
 *        - intermediate   → second path (if it exists & is unlocked)
 *        - advanced       → third path (if it exists & is unlocked)
 *      Falls back to earlier paths if everything later is already complete.
 *   4. Linear fallback: first incomplete unlocked lesson anywhere.
 *
 * Streak signal: when the user hasn't done a lesson today and the streak is
 * alive, we still resume the same lesson — the goal is "one click → start" —
 * but we flag it via `streakAtRisk` so the CTA can render an urgent label.
 */
function findNextLesson(
  userId: string | undefined,
  sport: string,
  completedLessons: string[],
  xp: number,
  ctx: NextLessonContext,
) {
  const paths = getPathsForSport(sport);
  const completedSet = new Set(completedLessons);

  let totalCount = 0;
  const positionById = new Map<
    string,
    { position: number; pathId: string; pathName: string; pathIndex: number; requiredXp: number; lesson: any }
  >();

  paths.forEach((path, pathIndex) => {
    for (const unit of path.units) {
      for (const lesson of unit.lessons) {
        totalCount++;
        positionById.set(lesson.id, {
          position: totalCount,
          pathId: path.id,
          pathName: path.title,
          pathIndex,
          requiredXp: unit.requiredXp,
          lesson,
        });
      }
    }
  });

  const streakAtRisk = ctx.streak > 0 && !ctx.hasDoneLessonToday;

  const pack = (entry: NonNullable<ReturnType<typeof positionById.get>>, source: 'resume' | 'same-path' | 'skill-default' | 'linear') => ({
    lesson: entry.lesson,
    pathName: entry.pathName,
    lessonNumber: entry.position,
    totalLessons: totalCount,
    source,
    streakAtRisk,
  });

  const firstIncompleteInPath = (pathIndex: number) => {
    const path = paths[pathIndex];
    if (!path) return null;
    for (const unit of path.units) {
      if (xp < unit.requiredXp) continue;
      for (const lesson of unit.lessons) {
        if (!completedSet.has(lesson.id)) return positionById.get(lesson.id)!;
      }
    }
    return null;
  };

  // 1. Resume the last-attempted lesson if still actionable
  const lastId = typeof window !== 'undefined'
    ? localStorage.getItem(lastAttemptedLessonKey(userId, sport))
    : null;
  const lastEntry = lastId ? positionById.get(lastId) : undefined;

  if (lastEntry && !completedSet.has(lastId!) && xp >= lastEntry.requiredXp) {
    return pack(lastEntry, 'resume');
  }

  // 2. Continue inside the same path the user was last on
  if (lastEntry) {
    const samePath = firstIncompleteInPath(lastEntry.pathIndex);
    if (samePath) return pack(samePath, 'same-path');
  }

  // 3. Skill-aware default starting path
  const skillStartIndex =
    ctx.skillLevel === 'advanced' ? 2 : ctx.skillLevel === 'intermediate' ? 1 : 0;
  for (let i = skillStartIndex; i < paths.length; i++) {
    const hit = firstIncompleteInPath(i);
    if (hit) return pack(hit, 'skill-default');
  }
  // ...and earlier paths if everything later is already done
  for (let i = skillStartIndex - 1; i >= 0; i--) {
    const hit = firstIncompleteInPath(i);
    if (hit) return pack(hit, 'skill-default');
  }

  // 4. Pure linear fallback (covers unlocked-but-skipped edge cases)
  for (let i = 0; i < paths.length; i++) {
    const hit = firstIncompleteInPath(i);
    if (hit) return pack(hit, 'linear');
  }

  return {
    lesson: null,
    pathName: '',
    lessonNumber: totalCount,
    totalLessons: totalCount,
    source: 'linear' as const,
    streakAtRisk,
  };
}


export function HomeTab({ onSelectLesson, onNavigate, preferredSport = 'football' }: HomeTabProps) {
  const { user } = useAuth();
  const {
    progress,
    claimStreakMilestone,
    levelName,
    weakAreas,
    loaded,
  } = useProgress();

  useAchievements(progress);
  const { isPlayer, playerProfile } = usePlayerProfiles();
  const { getLevel } = useSportSkillLevel();

  // Greet the learner by name — child accounts carry their parent-set first name.
  const firstName = (playerProfile?.display_name || playerProfile?.username || '')
    .trim()
    .split(' ')[0];
  const isNewLearner = progress.xp === 0 && progress.completedLessons.length === 0;


  // Streak milestone popup
  const milestone = getReachedMilestone(progress.streak);
  const [milestoneShown, setMilestoneShown] = useState<number | null>(null);
  const [showMilestonePopup, setShowMilestonePopup] = useState(false);

  useEffect(() => {
    if (milestone && milestone.days !== milestoneShown) {
      const claimedKey = `streak_milestone_${user?.id ?? 'anon'}_${milestone.days}_claimed`;
      if (!localStorage.getItem(claimedKey)) {
        setShowMilestonePopup(true);
        setMilestoneShown(milestone.days);
      }
    }
  }, [milestone, milestoneShown, user?.id]);

  // Welcome-back nudge → moved to subtle sonner toast (no longer a top banner)
  const today = new Date().toISOString().split('T')[0];
  const daysAway = (() => {
    if (!progress.lastActiveDate) return 0;
    const last = new Date(progress.lastActiveDate);
    const now = new Date(today);
    return Math.floor((now.getTime() - last.getTime()) / 86400000);
  })();

  useEffect(() => {
    if (daysAway >= 1 && progress.lastActiveDate) {
      const nudgeKey = `welcome_back_${user?.id ?? 'anon'}_${today}`;
      if (!sessionStorage.getItem(nudgeKey)) {
        const lostStreak = progress.streak === 0 && daysAway > 1;
        if (lostStreak) {
          toast('Your streak was reset', {
            description: 'No worries — start a new one today with just one lesson!',
            position: 'bottom-right',
            duration: 6000,
          });
        }
        sessionStorage.setItem(nudgeKey, 'true');
      }
    }
  }, [daysAway, progress.lastActiveDate, progress.streak, today, user?.id]);


  // Daily goal = distinct LESSONS completed today (videos/articles from a coach
  // are assignments, not daily-goal activities — that is an intentional rule).
  // Derived from question history timestamps, which is the only per-day record
  // of lesson activity we persist.
  const lessonsToday = useMemo(() => {
    const completed = new Set(progress.completedLessons);
    const ids = new Set<string>();
    for (const q of progress.questionHistory ?? []) {
      if (q.timestamp?.startsWith(today) && completed.has(q.lessonId)) ids.add(q.lessonId);
    }
    return ids.size;
  }, [progress.completedLessons, progress.questionHistory, today]);


  const dailyTarget = 3;


  // Next lesson for the active sport — context-aware (path + skill + streak)
  const skillLevel = getLevel(preferredSport) as 'beginner' | 'intermediate' | 'advanced' | null;
  const hasDoneLessonToday = lessonsToday > 0;
  const next = useMemo(
    () => findNextLesson(user?.id, preferredSport, progress.completedLessons, progress.xp, {
      skillLevel,
      streak: progress.streak,
      hasDoneLessonToday,
    }),
    [user?.id, preferredSport, progress.completedLessons, progress.xp, skillLevel, progress.streak, hasDoneLessonToday]
  );

  if (!loaded) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 rounded-2xl bg-muted/50 animate-pulse" />
        ))}
      </div>
    );
  }

  
  const sportEmoji = sports.find(s => s.id === preferredSport)?.emoji || '🏈';
  const allComplete = !next.lesson;
  const goalPct = Math.min((lessonsToday / dailyTarget) * 100, 100);

  const handleContinue = () => {
    if (next.lesson) {
      onSelectLesson(next.lesson.id);
    } else {
      onNavigate('learn');
    }
  };

  return (
    <div className={isPlayer ? 'space-y-4' : 'space-y-5'}>
      {/* Streak Milestone Popup */}
      {showMilestonePopup && milestone && (
        <StreakMilestonePopup
          milestone={milestone}
          onClaim={async () => {
            // Server validates streak + assigns reward; client cannot pick the amount.
            await claimStreakMilestone(milestone.days);
            localStorage.setItem(`streak_milestone_${user?.id ?? 'anon'}_${milestone.days}_claimed`, 'true');
            setShowMilestonePopup(false);
          }}
          onDismiss={() => setShowMilestonePopup(false)}
        />
      )}

      {/* ── Welcome Card ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`glass-card shadow-elevated relative overflow-hidden ${isPlayer ? 'p-4' : 'p-5'}`}
      >
        <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-primary/15 blur-2xl pointer-events-none" />
        <div className="relative flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-blue-400 flex items-center justify-center shadow-glow-primary shrink-0">
            <Sparkles className="w-6 h-6 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`font-display font-bold text-foreground leading-tight ${isPlayer ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'}`}>
              {isNewLearner
                ? `Welcome${firstName ? `, ${firstName}` : ''}! Start your first lesson 🎉`
                : `Welcome back${firstName ? `, ${firstName}` : ''}! Ready to keep learning?`}
            </h2>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-xs font-display font-bold text-foreground/70">{levelName}</span>
              <span className="text-foreground/30">·</span>
              <span className="text-xs display-number text-primary">
                {isNewLearner ? '0 XP — earn your first XP today' : `${progress.xp.toLocaleString()} XP`}
              </span>
              <span className="text-foreground/30">·</span>
              <span className="text-xs display-number text-warning flex items-center gap-1">
                <Flame className="w-3 h-3" />
                {progress.streak > 0 ? progress.streak : 'No streak yet — today counts!'}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <HeartsDisplay hearts={progress.hearts} />
              <span className="text-[11px] font-body text-foreground/50">
                {progress.hearts >= 5 ? 'Full energy' : `${progress.hearts} of 5 lives`}
              </span>
            </div>
          </div>

        </div>
      </motion.div>

      {/* ── From Your Coach — unified banner per coach (lessons + content) ── */}
      <CoachBanner onSelectLesson={onSelectLesson} />


      {/* ── Smart weak-spot one-liner (full Strengths & Gaps lives in Learn) ── */}
      {weakAreas.length > 0 && (() => {
        const weakest = [...weakAreas].sort((a, b) => a.accuracy - b.accuracy)[0];
        if (!weakest || weakest.accuracy >= 80) return null;
        return (
          <motion.button
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.04 }}
            onClick={() => onNavigate('learn')}
            className="w-full glass-card flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors"
          >
            <AlertCircle className="w-4 h-4 text-warning shrink-0" />
            <span className="text-xs font-body text-foreground/80 flex-1 truncate">
              You're weakest in <span className="font-display font-bold text-foreground">{weakest.label}</span> — want to practice?
            </span>
            <span className="text-[11px] font-display font-bold text-primary shrink-0">Practice →</span>
          </motion.button>
        );
      })()}




      {/* ── 2. Hero Focus Card ── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        whileHover={{ y: -2, transition: { duration: 0.2 } }}
        className={`glass-card relative overflow-hidden p-6 shadow-elevated transition-shadow hover:shadow-glow-primary`}
      >
        <div className="relative z-10 flex flex-col items-center text-center gap-4">
          <motion.span
            className="text-6xl drop-shadow-lg"
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            {sportEmoji}
          </motion.span>

          {allComplete ? (
            <>
              <div>
                <h2 className="font-display font-extrabold text-xl text-foreground">All caught up! 🎉</h2>
                <p className="text-sm text-muted-foreground font-body mt-1">
                  You've completed every available lesson. New content drops soon!
                </p>
              </div>
              <motion.div whileTap={{ scale: 0.97 }} className="w-full">
                <Button
                  onClick={() => onNavigate('learn')}
                  className={`shimmer-cta w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl shadow-glow-primary transition-transform hover:-translate-y-0.5`}
                >
                  Review Lessons
                </Button>
              </motion.div>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <h2 className="font-display font-extrabold text-xl text-foreground leading-tight">
                  {next.lesson!.title}
                </h2>
                <p className="text-sm text-muted-foreground font-body">
                  Lesson {next.lessonNumber} of {next.totalLessons} · {next.pathName}
                </p>
                <div className="flex items-center justify-center gap-2 pt-1">
                  <span className="text-[11px] font-body text-foreground/60 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
                    ⏱ 5 min lesson
                  </span>
                  <span className="text-[11px] font-display font-bold text-accent bg-accent/15 px-2 py-0.5 rounded-full ring-1 ring-accent/30">
                    +15 XP
                  </span>
                </div>

              </div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} className="w-full">
                <Button
                  onClick={handleContinue}
                  className={`shimmer-cta w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl shadow-glow-primary transition-all`}
                >
                  <Zap className="mr-2 w-5 h-5" />
                  {(() => {
                    // Streak alive but no lesson done today → keep-the-streak nudge wins.
                    if (next.streakAtRisk) return 'Keep Your Streak';
                    if (next.source === 'resume' || next.source === 'same-path') return 'Continue Learning';
                    return "Start Today's Lesson";
                  })()}
                </Button>
              </motion.div>
            </>
          )}
        </div>

        {/* Decorative background circle */}
        <div className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full bg-foreground/[0.04]" />
        <div className="absolute -left-8 -top-8 w-28 h-28 rounded-full bg-primary/5 blur-2xl" />
      </motion.div>

      {/* ── 3. Daily Goal Strip ── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        whileHover={{ y: -1 }}
        className="glass-card px-4 py-4 shadow-elevated transition-shadow hover:shadow-glow-primary"
      >
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <span className="text-base">🎯</span>
            <span className="text-sm font-display font-bold text-foreground">Daily Goal</span>
          </div>
          <span className="text-sm font-display font-bold display-number text-foreground">
            {lessonsToday}<span className="text-foreground/40">/{dailyTarget}</span>
          </span>
        </div>
        <div className="relative w-full h-3 bg-white/5 border border-white/10 rounded-full overflow-hidden mb-2">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-primary via-blue-400 to-accent relative"
            initial={{ width: 0 }}
            animate={{ width: `${goalPct}%` }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>
          {/* Tick markers */}
          {Array.from({ length: dailyTarget - 1 }).map((_, i) => (
            <span
              key={i}
              className="absolute top-0 bottom-0 w-px bg-white/15"
              style={{ left: `${((i + 1) / dailyTarget) * 100}%` }}
              aria-hidden="true"
            />
          ))}
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[11px] font-body text-foreground/60">
            {goalPct >= 100
              ? '✓ Goal complete — nice work!'
              : `Complete ${dailyTarget - lessonsToday} more lesson${dailyTarget - lessonsToday > 1 ? 's' : ''} for +25 XP`}
          </p>
          <span className="text-[11px] font-display font-bold text-accent">🏆 +25 XP</span>
        </div>

      </motion.div>

      {/* ── 5. Weekly Recap (condensed single row) ── */}
      <WeeklyRecapCard onNavigate={onNavigate} />
    </div>
  );
}
